import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Monitor,
  Users,
  TrendingUp,
  ArrowUpCircle,
  Link2,
  Gauge,
  ClipboardList,
  Eye,
  Rocket,
  type LucideIcon,
} from "lucide-react";
import Header from "../../components/Header/Header";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import "./website-redesigning.css";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

type BenefitItem = {
  title: string;
  text: string;
  icon: LucideIcon;
};

type ProcessItem = {
  title: string;
  paragraphs: string[];
  icon: LucideIcon;
};

const benefits: BenefitItem[] = [
  {
    title: "Start a New",
    text: "Website redesigning gives you the chance to start over new and correct the mistakes that you might have made when you started the website years back.",
    icon: Monitor,
  },
  {
    title: "Wider Audience",
    text: "The digital world is moving at an extremely fast pace and if you want to survive in the race to be better than the rest, you need to adapt.",
    icon: Users,
  },
  {
    title: "Increase Conversion Rates",
    text: "The conversion rate of a website is effective for business profit. If your conversion rate is down, there might be many reasons for this such as navigation issues.",
    icon: TrendingUp,
  },
  {
    title: "Get Better Ranking",
    text: "A new website will also help you in getting better search engine rankings and reach the top 10. Not only do we redesign your website with new content but also make sure that it is optimized for search engines.",
    icon: ArrowUpCircle,
  },
  {
    title: "Reinforce Your Brand",
    text: "The redesigning of your website will also offer the chance of bringing changes to your brand image. The entire interface of the website can also be changed to make it more gripping and easily usable.",
    icon: Link2,
  },
  {
    title: "Boosts Morale",
    text: "A new website will not only delight your customers but also your employees. The new look and feel of the website will instill a sense of direction and make them work harder and better.",
    icon: Gauge,
  },
];

const processSections: ProcessItem[] = [
  {
    title: "Make Plans How to Proceed",
    icon: ClipboardList,
    paragraphs: [
      "To make sure that you don't lose direction or face confusion in the middle of the redesigning process, it is important to plan the process. As a top website redesign company in Bangalore, we will do a complete analysis of your website. You need to have a clear vision of how the website must appear once the redesigning is complete.",
      "An innovative way to plan the progress is to make a detailed sitemap of the various pages as well as the content which can be referred to from time to time. This will help you to stay on track. A proper plan will also prevent interruptions and distractions in the middle of redesigning.",
      "This is known as cross-browser testing and is a great way to check out how effective the redesigning was. The new website must be tested on different browsers and devices to ensure that it doesn't face any problems when accessed from anywhere. If you find any mistakes or issues, our team will be more than ready to address and rectify them. We will not stop till your redesigned website is free of all possible issues and satisfactory to you.",
    ],
  },
  {
    title: "Analyzing and Observing",
    icon: Eye,
    paragraphs: [
      "Once the new website goes online, this is not the end of the redesigning process. You need to keep a keen eye on how the revamped site is working. Take note of the things that seem to be working and those that do not.",
      "The redesigning of a website is a continuous process as you cannot let it become stagnant. You will want to keep up with trends and keep the website improving and growing over the years.",
    ],
  },
  {
    title: "Launching the Re-Designed Website",
    icon: Rocket,
    paragraphs: [
      "When the redesigning has been done, you would want to let the people know about the changes and improvements that have been made to your website. Market your revamped website to reintroduce your business to your existing as well as target audience.",
      "The main objective must be to make as many people aware of the exciting new interface of your site as you can. This will drive traffic to your site.",
    ],
  },
];

const AnimatedSection: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className = "", children }) => (
  <motion.section
    className={className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    variants={fadeUp}
    transition={{ duration: 0.6, ease: "easeOut" }}
  >
    {children}
  </motion.section>
);

const WebsiteRedesigningPage: React.FC = () => {
  useEffect(() => {
    document.title =
      "Website Redesigning Services | WebTech Software Solutions Bangalore";
    const description =
      "WebTech is the best website redesigning company in Bangalore. Transform your outdated website with modern design, new features, and SEO-ready upgrades.";
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, []);

  return (
    <Fragment>
      <div className="wredesign-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            <section className="wredesign-hero" aria-labelledby="wredesign-hero-title">
              <div className="wredesign-hero__bg" aria-hidden="true" />
              <div className="wredesign-container wredesign-hero__inner">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.65 }}
                >
                  <nav className="wredesign-breadcrumb" aria-label="Breadcrumb">
                    <Link to="/">Home</Link>
                    <span>/</span>
                    <Link to="/service">Services</Link>
                  </nav>
                  <span className="wredesign-label">Website Redesigning</span>
                  <h1 id="wredesign-hero-title">
                    Website Redesigning <span>Services</span>
                  </h1>
                  <p>
                    Transform your outdated website into a modern, high-performing
                    digital experience that keeps you ahead of the competition.
                  </p>
                  <Link className="wredesign-btn wredesign-btn--primary" to="/contact">
                    Get Free Consultation
                  </Link>
                </motion.div>
              </div>
            </section>

            <AnimatedSection className="wredesign-section wredesign-section--white">
              <div className="wredesign-container wredesign-intro">
                <div className="wredesign-intro__content">
                  <span className="wredesign-label wredesign-label--dark">
                    Best in Bangalore
                  </span>
                  <h2>
                    WebTech Software Solutions is the best website redesigning
                    company in Bangalore
                  </h2>
                  <p>
                    Just like all the other aspects of maintaining a successful
                    business, your website too requires redesigning every once in a
                    while. There are many things that change with time and the same
                    website that you launched five years ago might now have turned
                    obsolete. In order to ensure that you don&apos;t lose your customer
                    base to the newly launched and modern looking websites, hire
                    WebTech to transform your old website and introduce newly
                    introduced features and elements.
                  </p>
                  <p>
                    Known as one of the best professional services in Bangalore, we
                    understand the need of revamping your website from time to time
                    and providing necessary upgrades. The digital world is progressing
                    so fast that sometimes it might be hard to keep up with its
                    lightning speed. This is why you need to consciously keep up with
                    the advances in technology. This will allow you to stay afloat in
                    this highly competitive era and hold your own against your
                    competition.
                  </p>
                </div>
                <div className="wredesign-intro__visual" aria-hidden="true">
                  <div className="wredesign-preview">
                    <div className="wredesign-preview__old">
                      <span>Before</span>
                      <div className="wredesign-preview__screen wredesign-preview__screen--old" />
                    </div>
                    <div className="wredesign-preview__arrow">→</div>
                    <div className="wredesign-preview__new">
                      <span>After</span>
                      <div className="wredesign-preview__screen wredesign-preview__screen--new" />
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="wredesign-section wredesign-section--benefits">
              <div className="wredesign-container">
                <div className="wredesign-section-head wredesign-section-head--center wredesign-section-head--light">
                  <h2>Benefits of Website Redesigning</h2>
                  <p>
                    Redesigning your website helps you stay competitive, reach a wider
                    audience, and deliver a modern experience that converts visitors
                    into loyal customers.
                  </p>
                </div>
                <motion.div className="wredesign-benefit-grid" variants={stagger}>
                  {benefits.map(({ title, text, icon: Icon }) => (
                    <motion.article
                      className="wredesign-benefit-card"
                      variants={fadeUp}
                      key={title}
                    >
                      <div className="wredesign-benefit-card__icon">
                        <Icon size={28} strokeWidth={1.8} aria-hidden="true" />
                      </div>
                      <h3>{title}</h3>
                      <p>{text}</p>
                    </motion.article>
                  ))}
                </motion.div>
              </div>
            </AnimatedSection>

            {processSections.map((section, index) => (
              <AnimatedSection
                className={`wredesign-section ${
                  index % 2 === 0
                    ? "wredesign-section--white"
                    : "wredesign-section--soft"
                }`}
                key={section.title}
              >
                <div className="wredesign-container wredesign-process">
                  <div className="wredesign-process__icon" aria-hidden="true">
                    <section.icon size={32} strokeWidth={1.8} />
                  </div>
                  <div className="wredesign-process__content">
                    <h2>{section.title}</h2>
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            ))}

            <AnimatedSection className="wredesign-cta">
              <div className="wredesign-container wredesign-cta__inner">
                <div>
                  <span>Ready to Revamp?</span>
                  <h2>Transform Your Website Today</h2>
                  <p>
                    Partner with WebTech to redesign your website with modern design,
                    better performance, and features that drive real business growth.
                  </p>
                </div>
                <Link className="wredesign-btn wredesign-btn--white" to="/contact">
                  Start Your Redesign
                </Link>
              </div>
            </AnimatedSection>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default WebsiteRedesigningPage;
